// Movimientos de inventario por restaurante (RES-24): tipos, motivos de merma, respuesta paginada
// y link al origen (pedido / orden de compra / transferencia). Sin dependencias de React para
// poder testearlo puro.

/** Tipos persistidos por el backend, en el orden del filtro. */
export const MOVEMENT_TYPES = [
  'STOCK_IN',
  'STOCK_OUT',
  'ADJUSTMENT',
  'WASTE',
  'COUNT',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'SALE',
  'SALE_REVERSAL',
];

/**
 * Tipos que se pueden registrar a mano (TRANSFER genera TRANSFER_OUT + TRANSFER_IN). La merma va
 * segunda para que registrar un vencimiento sean pocos clics (RES-41).
 */
export const MANUAL_TYPES = ['STOCK_IN', 'WASTE', 'ADJUSTMENT', 'STOCK_OUT', 'COUNT', 'TRANSFER'];

/** Motivos predefinidos de una merma. */
export const WASTE_REASONS = [
  'EXPIRED',
  'BREAKAGE',
  'FAILED_PREP',
  'INTERNAL_USE',
  'COURTESY',
  'OTHER',
];

/** Motivos codificados que no son de merma: el stock inicial que carga el wizard (RES-38). */
export const INITIAL_STOCK_REASON = 'INITIAL_STOCK';

/** Tope de filas del export CSV (RES-41). */
export const MAX_EXPORT_ROWS = 5000;

/** Tipos cuya cantidad es un stock absoluto (el form pide "stock real"). */
export const ABSOLUTE_TYPES = ['ADJUSTMENT', 'COUNT'];

/** Clave i18n del nombre de cada tipo (`inventory.movementsPage.*`). */
export const TYPE_LABEL_KEYS = {
  STOCK_IN: 'typeStockIn',
  STOCK_OUT: 'typeStockOut',
  ADJUSTMENT: 'typeAdjustment',
  WASTE: 'typeWaste',
  COUNT: 'typeCount',
  TRANSFER_OUT: 'typeTransferOut',
  TRANSFER_IN: 'typeTransferIn',
  SALE: 'typeSale',
  SALE_REVERSAL: 'typeSaleReversal',
};

/** Clase CSS del badge de cada tipo. */
export const typeBadgeClass = (type) =>
  String(type || '')
    .toLowerCase()
    .replace(/_/g, '-');

/**
 * Normaliza la respuesta de `GET /inventory/movements`: el backend nuevo devuelve
 * `{ items, nextToken, restaurantId }`; el anterior devolvía el array directo.
 * @param {Array|Object|null|undefined} data - Respuesta
 * @returns {{items: Array, nextToken: string|null, restaurantId: string|null}}
 */
export function normalizeMovementsResponse(data) {
  if (Array.isArray(data)) return { items: data, nextToken: null, restaurantId: null };
  if (!data || typeof data !== 'object') return { items: [], nextToken: null, restaurantId: null };
  return {
    items: Array.isArray(data.items) ? data.items : [],
    nextToken: data.nextToken || null,
    restaurantId: data.restaurantId || null,
  };
}

/**
 * Link al origen del movimiento según `RefType`.
 * @param {Object} movement - Movimiento (`RefType`, `RefId`)
 * @returns {{to: string, labelKey: string}|null} Ruta interna y clave i18n del title, o null
 */
export function movementRefLink(movement) {
  if (!movement || !movement.RefId) return null;
  switch (movement.RefType) {
    case 'ORDER':
      return {
        to: `/orders?search=${encodeURIComponent(movement.RefId)}`,
        labelKey: 'inventory.movementsPage.viewOrder',
      };
    case 'PURCHASE_ORDER':
      return {
        to: `/inventory/purchase-orders/${encodeURIComponent(movement.RefId)}`,
        labelKey: 'inventory.movementsPage.viewPurchaseOrder',
      };
    case 'TRANSFER':
      // No hay pantalla de transferencias: se ven sus dos patas filtrando por la referencia
      return {
        to: `/inventory/movements?refId=${encodeURIComponent(movement.RefId)}`,
        labelKey: 'inventory.movementsPage.viewTransfer',
      };
    default:
      return null;
  }
}

/**
 * Clave i18n del motivo codificado de un movimiento: el de la merma o "Stock inicial".
 * @param {Object} movement - Movimiento (`Type`, `ReasonCode`)
 * @returns {string|null} Clave i18n o null si no tiene motivo codificado
 */
export function reasonCodeLabelKey(movement) {
  if (!movement || !movement.ReasonCode) return null;
  if (movement.ReasonCode === INITIAL_STOCK_REASON) return 'inventory.movementsPage.initialStock';
  if (movement.Type === 'WASTE' && WASTE_REASONS.includes(movement.ReasonCode)) {
    return `inventory.movementsPage.wasteReasons.${movement.ReasonCode}`;
  }
  return null;
}

/**
 * Parámetros de `GET /inventory/movements` desde los filtros de la pantalla.
 * @param {Object} filters - `{ itemId, type, refId, from, to }`
 * @param {string|null} token - `nextToken` de la página a pedir
 * @param {number} limit - Tamaño de página
 * @returns {Object} Parámetros (los vacíos van como undefined)
 */
export function movementQueryParams(filters, token, limit) {
  return {
    itemId: filters.itemId || undefined,
    type: filters.type || undefined,
    refId: filters.refId || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    limit,
    nextToken: token || undefined,
  };
}

/**
 * Restaurante "del otro lado" de una transferencia (para mostrar "→ Sucursal B").
 * @param {Object} movement - Movimiento
 * @returns {{direction: 'to'|'from', restaurantId: string}|null}
 */
export function transferCounterpart(movement) {
  if (!movement) return null;
  if (movement.Type === 'TRANSFER_OUT' && movement.ToRestaurantId) {
    return { direction: 'to', restaurantId: movement.ToRestaurantId };
  }
  if (movement.Type === 'TRANSFER_IN' && movement.FromRestaurantId) {
    return { direction: 'from', restaurantId: movement.FromRestaurantId };
  }
  return null;
}

const hasValue = (value) => value !== '' && value !== undefined && value !== null;

/**
 * Arma el payload de `POST /inventory/movements` desde el form del modal.
 * En un ingreso el costo se carga como total del lote (`costMode: 'total'`, default) o por unidad
 * (`costMode: 'unit'` → `unitCost`); `purchaseOrderId` lo enlaza con la orden de compra (RES-41).
 * @param {Object} form - `{ itemId, type, quantity, reason, cost, costMode, expirationDate, reasonCode, toRestaurantId, purchaseOrderId }`
 * @returns {Object} Payload sin campos vacíos
 */
export function buildMovementPayload(form) {
  const payload = {
    itemId: form.itemId,
    type: form.type,
    quantity: parseFloat(form.quantity),
    reason: form.reason || undefined,
  };
  if (form.type === 'STOCK_IN') {
    if (hasValue(form.cost)) {
      if (form.costMode === 'unit') payload.unitCost = parseFloat(form.cost);
      else payload.cost = parseFloat(form.cost);
    }
    if (form.expirationDate) payload.expirationDate = form.expirationDate;
    if (form.purchaseOrderId) {
      payload.refType = 'PURCHASE_ORDER';
      payload.refId = form.purchaseOrderId;
    }
  }
  if (form.type === 'WASTE') payload.reasonCode = form.reasonCode || 'OTHER';
  if (form.type === 'TRANSFER') payload.toRestaurantId = form.toRestaurantId;
  return payload;
}
